import { h } from 'preact';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { TypingIndicator } from './TypingIndicator';
import { TypewriterText } from './TypewriterText';
import { FormattedChatText } from './FormattedChatText';

export type ChatMessage = {
  id: string;
  serverId?: string;
  clientRequestId?: string;
  role: 'user' | 'model' | 'system';
  content: string;
  contentJson?: any;
  timestamp: number;
};

function getUnlockedPaidSpoiler(m: ChatMessage): any | null {
  const cj = (m as any)?.contentJson;
  const spoiler = cj && typeof cj === 'object' ? cj.paid_spoiler : null;
  if (!spoiler || typeof spoiler !== 'object') return null;
  return String(spoiler.status || '') === 'unlocked' ? spoiler : null;
}

function PaidSpoilerCard({ message }: { message: ChatMessage }) {
  const unlocked = getUnlockedPaidSpoiler(message);
  if (unlocked) {
    const media = unlocked.media && typeof unlocked.media === 'object' ? unlocked.media : null;
    const url = String(media?.display_url || media?.url || '').trim();
    if (!url) return null;
    return (
      <div class="paid-spoiler-card unlocked">
        <img class="paid-spoiler-image" src={url} alt="" loading="lazy" />
      </div>
    );
  }

  return null;
}

export function ChatMessageList({
  messages,
  onLastModelDone,
}: {
  messages: ChatMessage[];
  onLastModelDone?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mountedAt] = useState(Date.now());
  const lastDoneIdRef = useRef<string>('');

  // Scroll to bottom helper
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll when messages change
  useEffect(() => {
    const t = setTimeout(scrollToBottom, 30);
    return () => clearTimeout(t);
  }, [messages.length, messages[messages.length - 1]?.id, scrollToBottom]);

  // If the last model message is not animated (e.g. WS snapshot), still treat it as "done"
  // so delayed CTAs (paywall/premium) can appear reliably.
  useEffect(() => {
    const last = messages.length ? messages[messages.length - 1] : null;
    if (!last) return;
    if (last.role !== 'model') return;
    if (!String(last.content || '').trim()) return;
    const shouldAnimate = (last.timestamp >= mountedAt - 5000);
    if (shouldAnimate) return; // TypewriterText will call onDone
    if (lastDoneIdRef.current === String(last.id)) return;
    lastDoneIdRef.current = String(last.id);
    try { onLastModelDone?.(); } catch {}
  }, [messages.length, messages[messages.length - 1]?.id, messages[messages.length - 1]?.content, mountedAt, onLastModelDone]);

  return (
    <div ref={containerRef} class="chat-message-list">
      {messages.map((m, idx) => {
        const isLast = idx === messages.length - 1;
        const isModel = m.role === 'model';
        
        // Show TypingIndicator if model message is empty (waiting for stream)
        if (isModel && !m.content) {
          return (
            <div key={m.id} class={`chat-bubble ${m.role}`}>
              <TypingIndicator />
            </div>
          );
        }

        // Use Typewriter for latest model message if fresh
        const shouldAnimate = isModel && isLast && (m.timestamp >= mountedAt - 5000);

        const spoilerCard = isModel
          ? (
            <PaidSpoilerCard
              message={m}
            />
          )
          : null;

        return (
          <div key={m.id} class={`chat-bubble ${m.role}`}>
            {shouldAnimate ? (
              <>
                <TypewriterText
                  content={m.content}
                  animate={true}
                  onUpdate={scrollToBottom}
                  onDone={onLastModelDone}
                />
                {spoilerCard}
              </>
            ) : (
              isModel
                ? <><FormattedChatText content={m.content} />{spoilerCard}</>
                : m.content
            )}
          </div>
        );
      })}
    </div>
  );
}
